import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { X, Search as SearchIcon, ArrowUpRight, Tag } from "lucide-react";
import api from "../lib/api";
import { productUrl } from "../lib/seo";
import { useCategories } from "../hooks/useCategories";

// Maps user queries to canonical category slugs.
const CATEGORY_ALIASES = [
  { keys: ["pickle", "pickles", "avakaya", "achar"], category: "Pickles" },
  { keys: ["paste", "pastes", "culinary paste", "culinary pastes", "ginger garlic"], category: "Culinary Pastes" },
  { keys: ["spice", "spices", "chilli powder", "turmeric", "coriander"], category: "Spices" },
  { keys: ["masala", "masalas", "blend", "blends", "sambar", "rasam", "garam"], category: "Masalas" },
  { keys: ["podi", "podis", "karam", "pappula", "traditional powder", "traditional powders", "nalla karam", "idli karam", "kobbari"], category: "Traditional Powders" },
  { keys: ["flour", "flours", "ragi", "millet"], category: "Flours" },
  { keys: ["oil", "oils", "wood pressed", "wood-pressed", "wood pressed oils", "groundnut oil", "coconut oil"], category: "Wood Pressed Oils" },
];

function findCategoryMatch(query) {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  for (const { keys, category } of CATEGORY_ALIASES) {
    if (keys.some((k) => q === k || q.includes(k) || k.includes(q))) {
      return category;
    }
  }
  return null;
}

export default function SearchOverlay({ open, onClose }) {
  // iter76 — live categories from Admin → Categories.
  const liveCategories = useCategories();
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Load product list once
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    api.get("/products")
      .then((r) => { if (!cancel) setProducts(r.data || []); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [open]);

  // Auto-focus + lock body scroll when open
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Reset query when closed
  useEffect(() => { if (!open) setQuery(""); }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const { productMatches, categoryMatch } = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return { productMatches: [], categoryMatch: null };
    const cat = findCategoryMatch(q);
    const matches = products
      .filter((p) =>
        p.name?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q),
      )
      .slice(0, 8);
    return { productMatches: matches, categoryMatch: cat };
  }, [query, products]);

  const submit = (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q) return;
    // Priority: exact product match → that product page
    const exact = products.find((p) => p.name.toLowerCase() === q.toLowerCase());
    if (exact) {
      onClose();
      navigate(productUrl(exact));
      return;
    }
    // Category match → /shop?category=…
    const cat = findCategoryMatch(q);
    if (cat) {
      onClose();
      navigate(`/shop?category=${encodeURIComponent(cat)}`);
      return;
    }
    // Fallback: search results on Shop
    onClose();
    navigate(`/shop?search=${encodeURIComponent(q)}`);
  };

  const go = (path) => { onClose(); navigate(path); };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-brand-dark/55 backdrop-blur-sm animate-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="search-overlay"
    >
      <div className="mx-auto max-w-2xl w-[94%] sm:w-full mt-[8vh] sm:mt-[12vh] bg-brand-cream rounded-2xl shadow-2xl overflow-hidden">
        <form onSubmit={submit} className="flex items-center gap-3 px-5 py-4 border-b border-brand-line">
          <SearchIcon className="w-5 h-5 text-brand-muted flex-shrink-0" strokeWidth={1.7} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            placeholder="Search products or categories…"
            className="flex-1 bg-transparent outline-none text-base text-brand-dark placeholder:text-brand-muted/70 py-2"
            data-testid="search-input"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close search"
            className="p-2 rounded-full text-brand-dark transition-colors active:bg-brand-chili active:text-white"
            data-testid="search-close"
          >
            <X className="w-5 h-5" strokeWidth={1.7} />
          </button>
        </form>

        <div className="max-h-[60vh] overflow-y-auto" data-testid="search-results">
          {query.trim() === "" ? (
            <div className="px-5 py-6">
              <p className="overline mb-3">Browse by category</p>
              {/*
                iter89/iter90 — Category chip layout.
                Mobile: tighter chips + left-aligned wrap → 3 rows.
                Desktop: same padding, still left-aligned (overlay is
                narrower than viewport so it naturally reads left-to-right).
              */}
              <div className="flex flex-wrap justify-start gap-x-1.5 gap-y-2 sm:gap-x-2 max-w-2xl">
                {liveCategories.map((cat) => cat.name).map((c) => (
                  <button
                    key={c}
                    onClick={() => go(`/shop?category=${encodeURIComponent(c)}`)}
                    className="px-3 sm:px-3.5 py-1.5 rounded-full text-xs sm:text-sm bg-white border border-brand-line text-brand-dark transition-colors hover:border-brand-chili/60 active:bg-brand-chili active:text-white active:border-brand-chili whitespace-nowrap"
                    data-testid={`search-cat-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {categoryMatch && (
                <button
                  type="button"
                  onClick={() => go(`/shop?category=${encodeURIComponent(categoryMatch)}`)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 border-b border-brand-line text-left transition-colors active:bg-brand-subtle"
                  data-testid="search-category-result"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-brand-chili/10 text-brand-chili flex items-center justify-center">
                      <Tag className="w-4 h-4" strokeWidth={1.7} />
                    </span>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-brand-muted">Collection</div>
                      <div className="font-serif text-lg text-brand-dark">{categoryMatch}</div>
                    </div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-brand-muted" strokeWidth={1.7} />
                </button>
              )}

              {productMatches.length === 0 && !categoryMatch ? (
                <div className="px-5 py-10 text-center text-brand-muted text-sm" data-testid="search-no-results">
                  No products match "{query}". Press Enter to search the full shop.
                </div>
              ) : (
                <ul className="divide-y divide-brand-line">
                  {productMatches.map((p) => {
                    const img = (p.images && p.images[0]) || p.image_url;
                    const price = (p.variants && p.variants[0]?.price) || p.price;
                    return (
                      <li key={p.product_id}>
                        <button
                          type="button"
                          onClick={() => go(productUrl(p))}
                          className="w-full flex items-center gap-4 px-5 py-3 text-left transition-colors active:bg-brand-subtle"
                          data-testid={`search-product-${p.product_id}`}
                        >
                          <img src={img} alt="" className="w-12 h-12 rounded-lg object-contain bg-white border border-brand-line p-1" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-brand-dark truncate">{p.name}</div>
                            <div className="text-xs text-brand-muted">{p.category} · from ₹{Number(price).toFixed(0)}</div>
                          </div>
                          <ArrowUpRight className="w-4 h-4 text-brand-muted flex-shrink-0" strokeWidth={1.7} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
